import { sheetV1 } from '@otto-ec/otto-components/sheet';
import { createImageUrl } from '../../../_common/server/tools/create-image-url';
import { trackEvent } from '@otto-ec/nexus-registry/pdp/tracking';
import '../../../_feature-components/ai-loader';
import { DialogProgressController } from './dialog-progress-controller';
import { isFileSharingSupported, saveImageFile, shareImageFile } from './generated-image-tools';

function trackClick(label: string) {
    trackEvent({ product_InteriorDesignConsultant: [label] });
}

function trackAutomatic(label: string) {
    trackEvent({ product_InteriorDesignConsultant: [label] }, { name: 'automatic', features: [] });
}

const ILLUSTRATION_PARAMS = 'h=768&sm=clamp&fmt=auto&qlt=40&unsharp=0,1,0.6,7';
const RENDERINGS_API_PATH = '/interior-desainer/prf/v2/renderings';
const UPLOAD_API_PATH = `${RENDERINGS_API_PATH}/upload`;
const IMAGES_API_PATH = `${RENDERINGS_API_PATH}/images`;
const GENERATION_TIMEOUT_MS = 5 * 60 * 1000;

const ERROR_MESSAGE = {
    unknown: 'Es ist ein unbekannter Fehler aufgetreten.',
    generationFailed: 'Die Bildgenerierung ist fehlgeschlagen. Bitte versuche es erneut.',
    missingImage: 'Keine Bilddaten empfangen.',
    malformedResult: 'Das Ergebnis konnte nicht verarbeitet werden.',
    connectionLost: 'Die Verbindung zum Server wurde unerwartet unterbrochen.',
    timeout: 'Die Bildgenerierung hat zu lange gedauert. Bitte versuche es erneut.',
} as const;

type SheetContext = {
    sheetElement: HTMLOcSheetV1Element;
    contentElement: HTMLElement;
    primaryButtonElement: HTMLOcButtonV1Element;
    secondaryButtonElement: HTMLOcButtonV1Element;
    variationId: string;
    uploadPromise?: Promise<string>;
    selectedFileObjectUrl?: string;
    progressController?: DialogProgressController;
    generatedImageBlob?: Blob;
    generatedImageUrl?: string;
};

function illustrationUrl(imageId: string) {
    return createImageUrl(imageId, ILLUSTRATION_PARAMS);
}

export async function openProductRoomFitSheet(variationId: string) {
    const { contentElement, actionsElement, primaryButtonElement, secondaryButtonElement } = createBasicSheetElements();

    const sheetElement = await sheetV1.create({
        id: 'pdp_product-room-fit__sheet',
        fullHeight: true,
    });

    const ctx: SheetContext = { sheetElement, contentElement, primaryButtonElement, secondaryButtonElement, variationId };

    sheetElement.replaceChildren(contentElement, actionsElement);
    showIntroStep(ctx);
    sheetElement.open = true;

    trackClick('Open');
}

function createBasicSheetElements() {
    const contentElement = document.createElement('div');
    contentElement.className = 'pdp_product-room-fit-sheet__content';

    const primaryButtonElement = document.createElement('oc-button-v1') as HTMLOcButtonV1Element;
    primaryButtonElement.className = 'pdp_product-room-fit-sheet__button';
    const secondaryButtonElement = document.createElement('oc-button-v1') as HTMLOcButtonV1Element;
    secondaryButtonElement.className = 'pdp_product-room-fit-sheet__button pl_hidden';

    const actionsElement = document.createElement('div');
    actionsElement.className = 'pdp_product-room-fit-sheet__actions';
    actionsElement.slot = 'actions';
    actionsElement.appendChild(secondaryButtonElement);
    actionsElement.appendChild(primaryButtonElement);

    return { contentElement, actionsElement: actionsElement, primaryButtonElement, secondaryButtonElement };
}

function showIntroStep(ctx: SheetContext) {
    ctx.sheetElement.headline = 'In meinen Raum generieren';

    ctx.primaryButtonElement.textContent = 'Weiter';
    ctx.primaryButtonElement.addEventListener(
        'click',
        () => {
            trackClick('Photo Upload Step 1');
            showConsentStep(ctx);
        },
        { once: true }
    );

    const img = document.createElement('img');
    img.className = 'pdp_product-room-fit-sheet__img pdp_product-room-fit-sheet__img--cover pdp_product-room-fit-sheet__img--full-height';
    img.src = illustrationUrl('illustration_augmented-reality');
    img.alt = 'Illustration eines Raumes mit Möbeln, die mithilfe von Augmented Reality visualisiert werden.';

    const figureContent = document.createElement('div');
    figureContent.className = 'pdp_product-room-fit-sheet__figure-content pdp_product-room-fit-sheet__figure-content--full-height';
    figureContent.appendChild(img);

    const figcaption = document.createElement('figcaption');
    figcaption.className = 'pdp_product-room-fit-sheet__figcaption pdp_product-room-fit-sheet__figcaption--text-center';
    figcaption.append(
        'Einfach ein Foto deines Raumes hochladen,',
        document.createElement('br'),
        'um Möbel direkt bei dir Zuhause zu erleben.'
    );

    const figure = document.createElement('figure');
    figure.className = 'pdp_product-room-fit-sheet__figure';
    figure.append(figureContent, figcaption);

    ctx.contentElement.replaceChildren(figure);
}

function showConsentStep(ctx: SheetContext) {
    ctx.sheetElement.headline = 'Bevor es losgeht';

    ctx.primaryButtonElement.disabled = true;
    ctx.primaryButtonElement.textContent = 'Los geht\u2019s';
    ctx.primaryButtonElement.addEventListener(
        'click',
        () => {
            trackClick('Photo Upload Step 2');
            showUploadStep(ctx);
        },
        { once: true }
    );

    const p = document.createElement('p');
    p.className = 'oc-mb-0';
    p.textContent =
        'Die dargestellten Bilder werden mithilfe künstlicher Intelligenz generiert und dienen ausschließlich der visuellen Veranschaulichung. Aussehen, Raumaufteilung, Materialien, Lichtverhältnisse sowie dargestellte Details können von der tatsächlichen Ausführung abweichen. Eventuell enthaltene persönliche oder dekorative Elemente entsprechen nicht zwingend der Realität.';

    const inputElement = document.createElement('input');
    inputElement.type = 'checkbox';
    inputElement.ariaLabel = 'oc-auto';

    const privacyPolicyUrl = '/static-proxy/product-assets/sheets/customer-review-policy.html';

    const link1 = document.createElement('oc-link-v2');
    link1.className = 'oc-secondary-action';
    link1.setAttribute('data-oc-sheet-v1-create', '');
    link1.setAttribute('data-oc-sheet-v1-create.url', privacyPolicyUrl);
    link1.textContent = 'Servicebedingungen';

    const link2 = document.createElement('oc-link-v2');
    link2.className = 'oc-secondary-action';
    link2.setAttribute('data-oc-sheet-v1-create', '');
    link2.setAttribute('data-oc-sheet-v1-create.url', privacyPolicyUrl);
    link2.textContent = 'Datenschutz- und Cookie-Richtlinien';

    const labelElement = document.createElement('label');
    labelElement.className = 'oc-copy-100';
    labelElement.slot = 'label';
    labelElement.append('Ich akzeptiere die ', link1, ' und bestätige, dass ich die ', link2, ' verstanden habe.');

    const checkbox = document.createElement('oc-checkbox-v2');
    checkbox.className = 'oc-mt-150';
    checkbox.append(inputElement, labelElement);

    ctx.contentElement.replaceChildren(p, checkbox);

    inputElement.addEventListener('change', event => {
        const target = event.target as HTMLInputElement;
        ctx.primaryButtonElement.disabled = !target.checked;
    });
}

function showUploadStep(ctx: SheetContext) {
    ctx.sheetElement.headline = 'In meinen Raum generieren';

    ctx.primaryButtonElement.textContent = 'Jetzt generieren';
    ctx.primaryButtonElement.iconTypeLeft = 'sparkles';
    ctx.primaryButtonElement.disabled = true;
    ctx.primaryButtonElement.addEventListener(
        'click',
        async () => {
            trackClick('Generation Initiated');
            showGeneratingStep(ctx);

            try {
                await startGeneration(ctx);
            } catch {
                showErrorStep(ctx);
            }
        },
        { once: true }
    );

    const icon = document.createElement('oc-icon-v1');
    icon.setAttribute('type', 'camera');
    icon.setAttribute('size', '100');

    const placeholder = document.createElement('div');
    placeholder.className = 'pdp_product-room-fit-sheet__placeholder';
    placeholder.append(icon, 'Foto auswählen');

    const inputElement = document.createElement('input');
    inputElement.id = 'pdp_product-room-fit-sheet__input';
    inputElement.type = 'file';
    inputElement.name = 'files';
    inputElement.accept = 'image/jpg,image/jpeg,image/png';

    const labelElement = document.createElement('label');
    labelElement.className = 'pdp_product-room-fit-sheet__figure-content';
    labelElement.htmlFor = 'pdp_product-room-fit-sheet__input';
    labelElement.title = '';
    labelElement.append(placeholder, inputElement);

    const figcaptionElement = document.createElement('figcaption');
    figcaptionElement.className = 'pdp_product-room-fit-sheet__figcaption';
    figcaptionElement.textContent = 'Lade ein Foto deines Raumes hoch, um zu sehen, wie das Produkt bei dir aussieht.';

    const figure = document.createElement('figure');
    figure.className = 'pdp_product-room-fit-sheet__figure';
    figure.style.setProperty('--text-align', 'center');
    figure.append(labelElement, figcaptionElement);

    ctx.contentElement.replaceChildren(figure);

    let uploadAbortController: AbortController | undefined;

    inputElement.addEventListener('change', event => {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];
        if (!file) return;

        if (ctx.selectedFileObjectUrl) {
            URL.revokeObjectURL(ctx.selectedFileObjectUrl);
        }
        uploadAbortController?.abort();
        uploadAbortController = new AbortController();
        ctx.selectedFileObjectUrl = URL.createObjectURL(file);
        ctx.uploadPromise = uploadImage(file, uploadAbortController.signal);
        ctx.uploadPromise.catch(() => {}); /*                               */

        labelElement.querySelector('.pdp_product-room-fit-sheet__placeholder')?.remove();
        labelElement.querySelector('img')?.remove();

        const img = document.createElement('img');
        img.className = 'pdp_product-room-fit-sheet__img';
        img.src = ctx.selectedFileObjectUrl;
        img.alt = 'Dein hochgeladenes Raumfoto.';
        labelElement.insertBefore(img, inputElement);

        figcaptionElement.textContent = 'Sehr gut! Im nächsten Schritt platzieren wir das ausgewählte Produkt in deinem Raum.';

        ctx.primaryButtonElement.disabled = false;
    });
}

function showGeneratingStep(ctx: SheetContext) {
    ctx.sheetElement.headline = 'Dein Raum wird erstellt';

    ctx.primaryButtonElement.loading = true;
    ctx.primaryButtonElement.textContent = '';
    ctx.primaryButtonElement.iconTypeLeft = undefined;

    const img = document.createElement('img');
    img.className = 'pdp_product-room-fit-sheet__img';
    img.src = ctx.selectedFileObjectUrl ?? '';
    img.alt = 'Dein hochgeladenes Raumfoto.';

    const loader = document.createElement('ai-loader');
    loader.className = 'pdp_product-room-fit-sheet__loader';
    loader.setAttribute('density', '300');

    const figureContent = document.createElement('div');
    figureContent.className = 'pdp_product-room-fit-sheet__figure-content pdp_product-room-fit-sheet__figure-content--blur';
    figureContent.append(img, loader);

    const figcaption = document.createElement('figcaption');
    figcaption.className = 'pdp_product-room-fit-sheet__figcaption';

    const figure = document.createElement('figure');
    figure.className = 'pdp_product-room-fit-sheet__figure';
    figure.append(figureContent, figcaption);

    ctx.contentElement.replaceChildren(figure);

    ctx.progressController = new DialogProgressController(figure, figcaption);
    ctx.progressController.start();
}

function showResultStep(ctx: SheetContext) {
    if (ctx.selectedFileObjectUrl) {
        URL.revokeObjectURL(ctx.selectedFileObjectUrl);
        ctx.selectedFileObjectUrl = undefined;
    }

    if (!ctx.generatedImageBlob || !ctx.generatedImageUrl) {
        showErrorStep(ctx, ERROR_MESSAGE.missingImage);
        return;
    }

    const { generatedImageBlob, generatedImageUrl } = ctx;

    ctx.sheetElement.headline = 'So sieht es bei dir aus';

    ctx.primaryButtonElement.loading = false;
    ctx.primaryButtonElement.textContent = 'Speichern';
    ctx.primaryButtonElement.addEventListener(
        'click',
        () => {
            trackClick('Generated Image Saved');
            saveImageFile(new File([generatedImageBlob], `mein-raum-${Date.now()}.png`, { type: 'image/png' }));
        },
        { once: true }
    );

    ctx.secondaryButtonElement.classList.toggle('pl_hidden', !isFileSharingSupported());
    ctx.secondaryButtonElement.textContent = 'Teilen';
    ctx.secondaryButtonElement.variant = 'secondary';
    ctx.secondaryButtonElement.addEventListener(
        'click',
        () => {
            trackClick('Generated Image Shared');
            shareImageFile(
                new File([generatedImageBlob], `mein-raum-${Date.now()}.png`, { type: 'image/png' }),
                'Mein Raum mit Otto'
            ).catch(console.error);
        },
        { once: true }
    );

    const img = document.createElement('img');
    img.className = 'pdp_product-room-fit-sheet__img';
    img.src = generatedImageUrl;
    img.alt = 'Das generierte Bild deines Raumes mit dem ausgewählten Produkt.';

    const figureContent = document.createElement('div');
    figureContent.className = 'pdp_product-room-fit-sheet__figure-content';
    figureContent.appendChild(img);

    const figcaption = document.createElement('figcaption');
    figcaption.className = 'pdp_product-room-fit-sheet__figcaption';
    figcaption.append(
        'Das generierte Bild kann von den echten Maßen abweichen.',
        document.createElement('br'),
        'Bitte prüfe die genauen Maße auf der Artikelseite.'
    );

    const figure = document.createElement('figure');
    figure.className = 'pdp_product-room-fit-sheet__figure';
    figure.append(figureContent, figcaption);

    ctx.contentElement.replaceChildren(figure);
}

function showErrorStep(ctx: SheetContext, message: string = ERROR_MESSAGE.unknown) {
    ctx.sheetElement.headline = 'Etwas ist schiefgelaufen';

    ctx.progressController?.stop();

    ctx.primaryButtonElement.loading = false;
    ctx.primaryButtonElement.textContent = 'Erneut versuchen';
    ctx.primaryButtonElement.iconTypeLeft = undefined;
    ctx.primaryButtonElement.addEventListener('click', () => showUploadStep(ctx), { once: true });

    ctx.secondaryButtonElement.classList.add('pl_hidden');

    const img = document.createElement('img');
    img.className = 'pdp_product-room-fit-sheet__img pdp_product-room-fit-sheet__img--cover pdp_product-room-fit-sheet__img--full-height';
    img.src = illustrationUrl('illustration_error');
    img.alt = 'Illustration eines Fehlers.';

    const figureContent = document.createElement('div');
    figureContent.className = 'pdp_product-room-fit-sheet__figure-content pdp_product-room-fit-sheet__figure-content--full-height';
    figureContent.appendChild(img);

    const figcaption = document.createElement('figcaption');
    figcaption.className = 'pdp_product-room-fit-sheet__figcaption';
    figcaption.textContent = message;

    const figure = document.createElement('figure');
    figure.className = 'pdp_product-room-fit-sheet__figure';
    figure.append(figureContent, figcaption);

    ctx.contentElement.replaceChildren(figure);
}

async function startGeneration(ctx: SheetContext) {
    if (!ctx.uploadPromise) {
        throw new Error('Generierung abgebrochen: Kein Upload im Kontext vorhanden.');
    }
    const imageRef = await ctx.uploadPromise;
    const response = await fetch(RENDERINGS_API_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageRef, varId: ctx.variationId }),
    });
    if (!response.ok) {
        throw new Error('Generierung fehlgeschlagen');
    }
    const { job_id } = await response.json();
    listenToGenerationProgress(ctx, job_id);
}

async function uploadImage(file: File, signal: AbortSignal): Promise<string> {
    const imageBytes = await fileToBase64(file);
    const response = await fetch(UPLOAD_API_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBytes }),
        signal,
    });
    if (!response.ok) {
        throw new Error('Upload fehlgeschlagen');
    }
    const data = await response.json();
    return data.imageRef;
}

async function fileToBase64(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}

function listenToGenerationProgress(ctx: SheetContext, taskId: string) {
    const eventSource = new EventSource(`${RENDERINGS_API_PATH}/${taskId}/events`);

    const timeout = window.setTimeout(() => finish(ERROR_MESSAGE.timeout), GENERATION_TIMEOUT_MS);

    function finish(errorMessage?: string) {
        clearTimeout(timeout);
        eventSource.close();
        ctx.progressController?.stop();

        if (errorMessage) {
            trackAutomatic('Generation Error');
            showErrorStep(ctx, errorMessage);
        } else {
            trackAutomatic('Generation Success');
        }
    }

    eventSource.addEventListener('progress', event => {
        try {
            const data = JSON.parse(event.data);
            if (data.percentage !== undefined) {
                ctx.progressController?.setProgress(data.percentage);
            }
        } catch (parseError) {
            console.error('Fehler beim Parsen der Progress-Daten:', parseError);
        }
    });

    eventSource.addEventListener('result', async event => {
        clearTimeout(timeout);
        eventSource.close();
        try {
            const { response } = JSON.parse(event.data);
            const imgRes = await fetch(`${IMAGES_API_PATH}/${response.imageRef}`);
            if (!imgRes.ok) {
                finish(ERROR_MESSAGE.malformedResult);
                return;
            }
            const { url } = await imgRes.json();
            if (!url) {
                finish(ERROR_MESSAGE.malformedResult);
                return;
            }
            const imageResponse = await fetch(url);
            if (!imageResponse.ok) {
                finish(ERROR_MESSAGE.malformedResult);
                return;
            }
            ctx.generatedImageBlob = await imageResponse.blob();
            ctx.generatedImageUrl = URL.createObjectURL(ctx.generatedImageBlob);
            finish();
            showResultStep(ctx);
        } catch (parseError) {
            console.error('Fehler beim Parsen der Result-Daten:', parseError);
            finish(ERROR_MESSAGE.malformedResult);
        }
    });

    eventSource.addEventListener('error', event => {
        /*                                                      */
        if (event instanceof MessageEvent) {
            let message: string = ERROR_MESSAGE.generationFailed;
            try {
                message = JSON.parse(event.data).response?.message ?? message;
            } catch {
                /*                        */
            }
            finish(message);
            return;
        }

        /*                                                                                  */
        if (eventSource.readyState === EventSource.CLOSED) {
            clearTimeout(timeout);
            return;
        }

        console.error('SSE-Verbindungsfehler:', event);
        finish(ERROR_MESSAGE.connectionLost);
    });

    eventSource.addEventListener('reconnect', () => {
        clearTimeout(timeout);
        eventSource.close();
        listenToGenerationProgress(ctx, taskId);
    });
}
